import React, { useState, useEffect } from 'react';
import {
   Accordion,
   AccordionSummary,
   AccordionDetails,
   Typography,
   Button,
   Box,
   Grid,
   TextField,
   MenuItem,
   Select,
   FormControl,
   InputLabel,
   IconButton,
   Alert,
   Dialog,
   DialogActions,
   DialogContent,
   DialogContentText,
   DialogTitle,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import {
   deleteMembership,
   fetchMembershipsForGroup,
   addMembership,
   deleteGroup,
   addGroup,
} from '../strapi/queries';

function getRoleName(role) {
   if (role == "admin") return "Administrator"
   if (role == "readOnly") return "Nur lesen"
   if (role == "checkOnly") return "Checken und lesen"
   return ""
}

export default function GroupManagementPage({ memberships, groupChangeKey }) {
   const [expanded, setExpanded] = useState(false);
   const [groupMemberships, setGroupMemberships] = useState({});
   const [newMembershipEmail, setNewMembershipEmail] = useState('');
   const [newMembershipRole, setNewMembershipRole] = useState('readOnly');
   const [errorMessage, setErrorMessage] = useState('');
   const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
   const [newGroupName, setNewGroupName] = useState('');
   const [newGroupDescription, setNewGroupDescription] = useState('');

   const handleChange = (panel) => (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
   };

   const loadGroupMemberships = async (groupId) => {
      setErrorMessage(''); // Clear previous errors
      try {
         const data = await fetchMembershipsForGroup(groupId);
         setGroupMemberships((prev) => ({ ...prev, [groupId]: data["data"].memberships }));
      } catch (error) {
         console.error('Error fetching group memberships:', error);
         setErrorMessage('Fehler beim Laden der Gruppenmitgliedschaften.');
      }
   };

   useEffect(() => {
      if (memberships && memberships.memberships) {
         memberships.memberships.forEach((membership) => {
            if (membership.role === 'admin') {
               loadGroupMemberships(membership.group.documentId);
            }
         });
      }
   }, [memberships, groupChangeKey]);

   const handleDeleteMembership = async (membershipId, groupId) => {
      setErrorMessage(''); // Clear previous errors
      try {
         await deleteMembership({ membership_id: membershipId });
         // Reload memberships for the specific group or remove the accordion item
         if (groupId) {
            loadGroupMemberships(groupId); // Reload for admin view
         } else {
            // For non-admin, remove the entire accordion item
            window.location.reload(); // Or more sophisticated state management
         }
      } catch (error) {
         console.error('Error deleting membership:', error);
         setErrorMessage('Fehler beim Löschen der Mitgliedschaft.');
      }
   };

   const handleAddMembership = async (groupId) => {
      setErrorMessage(''); // Clear previous errors
      try {
         await addMembership({
            group_id: groupId,
            user_email: newMembershipEmail,
            role: newMembershipRole,
         });
         setNewMembershipEmail('');
         setNewMembershipRole('readOnly');
         loadGroupMemberships(groupId); // Reload memberships for the group
      } catch (error) {
         console.error('Error adding membership:', error);
         setErrorMessage('Fehler beim Hinzufügen der Mitgliedschaft.');
      }
   };

   const handleDeleteGroup = async (groupId) => {
      setErrorMessage(''); // Clear previous errors
      try {
         await deleteGroup({ group_id: groupId });
         window.location.reload(); // Reload to remove the accordion item
      } catch (error) {
         console.error('Error deleting group:', error);
         setErrorMessage('Fehler beim Löschen der Gruppe.');
      }
   };

   const handleOpenAddGroupModal = () => {
      setIsAddGroupModalOpen(true);
      setNewGroupName('');
      setNewGroupDescription('');
      setErrorMessage('');
   };

   const handleCloseAddGroupModal = () => {
      setIsAddGroupModalOpen(false);
   };

   const handleAddGroup = async () => {
      setErrorMessage('');
      if (!newGroupName.trim()) {
         setErrorMessage('Gruppenname darf nicht leer sein.');
         return;
      }
      try {
         await addGroup({ name: newGroupName, desc: newGroupDescription });
         handleCloseAddGroupModal();
         window.location.reload(); // Reload to show the new group
      } catch (error) {
         console.error('Error adding group:', error);
         setErrorMessage('Fehler beim Erstellen der Gruppe.');
      }
   };

   if (!memberships || !memberships.memberships) {
      return <Typography>Loading groups...</Typography>;
   }

   return (
      <>
         <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
            <div className="index-page-container">
               <h2>Gruppen verwalten</h2>
               <p>
                  Verwalte deine Gruppen ganz einfach. Du kannst Gruppen erstellen und löschen, sowie Benutzer mit unterschiedlichen Rollen zuweisen und verwalten.
               </p>
               <Box sx={{ minWidth: "90px", display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                  <Button
                     variant="contained"
                     startIcon={<AddIcon />}
                     onClick={handleOpenAddGroupModal}
                     sx={{
                        border: "2px solid #10527e",
                        margin: "2px",
                        backgroundColor: "#10527e",
                        color: "white",
                        "&:hover": {
                           border: "2px solid #282c34",
                           backgroundColor: "#282c34",
                        },
                     }}
                  >
                     Neue Gruppe erstellen
                  </Button>
               </Box>
            </div>
            {errorMessage && (
               <Alert severity="error" sx={{ marginBottom: '20px' }}>
                  {errorMessage}
               </Alert>
            )}
            <div style={{
               display: 'flex',
               flexDirection: 'column', // untereinander anordnen
               alignItems: 'center',    // horizontal zentrieren
            }}>
               {memberships.memberships.map((membership) => (
                  <Box sx={{ width: "100%", maxWidth: "700px", justifyContent: "left" }}>
                     <Accordion
                        key={membership.documentId}
                        expanded={expanded === membership.documentId}
                        onChange={handleChange(membership.documentId)}
                        sx={{ marginBottom: '10px' }}
                     >
                        <AccordionSummary sx={{ alignItems: "center" }} expandIcon={<ExpandMoreIcon />}>
                           <Typography variant="h6">
                              {membership.group.name}
                           </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                           <Typography variant="subtitle1">
                              {membership.group.description &&
                                 ` ${membership.group.description}`}
                           </Typography>
                           {membership.group.description &&
                              <br></br>
                           }
                           {membership.role === 'readOnly' && (
                              <>
                                 <Typography>Du darfst diese Gruppe lesend betrachten.</Typography>
                                 <hr style={{ borderTop: "1px solid #eee", margin: "8px 0" }} />
                                 <Box sx={{ width: "100%", justifyContent: "center", alignItems: "center", display: "flex" }}>
                                    <Button
                                       variant="outlined"
                                       color="error"
                                       startIcon={<DeleteIcon />}
                                       onClick={() =>
                                          handleDeleteMembership(membership.documentId, null)
                                       }
                                       sx={{ marginTop: '10px' }}
                                    >
                                       Gruppe verlassen
                                    </Button>
                                 </Box>
                              </>
                           )}
                           {membership.role === 'checkOnly' && (
                              <>
                                 <Typography>
                                    Du darfst diese Gruppe lesend betrachten und Prüfungen
                                    durchführen.
                                 </Typography>
                                 <hr style={{ borderTop: "1px solid #eee", margin: "8px 0" }} />
                                 <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() =>
                                       handleDeleteMembership(membership.documentId, null)
                                    }
                                    sx={{ marginTop: '10px' }}
                                 >
                                    Gruppe verlassen
                                 </Button>
                              </>
                           )}
                           {membership.role === 'admin' && (
                              <>
                                 <Typography variant="subtitle1">Du bist Administrator in dieser Gruppe.</Typography>
                                 <hr style={{ borderTop: "1px solid #eee", margin: "15px 0" }} />
                                 <Typography variant="subtitle1">Aktuelle Mitglieder:</Typography>
                                 <Grid container spacing={2} sx={{ marginTop: '10px' }} alignItems="center">
                                    {groupMemberships[membership.group.documentId] &&
                                       groupMemberships[membership.group.documentId].map((groupMem) => (
                                          <React.Fragment key={groupMem.documentId}>
                                             <Grid container size={{ xs: 10 }} alignItems="center">
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                   <Typography variant="subtitle2">{groupMem.user.email}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 4 }}>
                                                   <Typography variant="subtitle2">{getRoleName(groupMem.role)}</Typography>
                                                </Grid>
                                             </Grid>
                                             <Grid container size={{ xs: 2 }} justifyContent="center" alignItems="center" sx={{ borderLeft: "2px solid #eee" }}>
                                                <IconButton
                                                   sx={{ color: "#a71717ff", marginLeft: "10px" }}
                                                   onClick={() =>
                                                      handleDeleteMembership(
                                                         groupMem.documentId,
                                                         groupMem.user.documentId == JSON.parse(localStorage.getItem("user"))["documentId"]
                                                            ? null
                                                            : membership.group.documentId
                                                      )
                                                   }
                                                >
                                                   <DeleteIcon />
                                                </IconButton>
                                             </Grid>
                                          </React.Fragment>
                                       ))}
                                 </Grid>
                                 <hr style={{ borderTop: "1px solid #eee", marginTop: "15px", marginBottom: "15px" }} />
                                 <Typography variant="subtitle1">Neues Mitglied hinzufügen:</Typography>
                                 <Grid size={{ xs: 12 }} spacing={2} sx={{ marginTop: '10px' }} alignItems="center">
                                    <Grid container alignItems="center" spacing={2}>
                                       <Grid size={{ xs: 6 }}>
                                          <TextField
                                             label="E-Mail"
                                             variant="outlined"
                                             size="small"
                                             value={newMembershipEmail}
                                             onChange={(e) =>
                                                setNewMembershipEmail(e.target.value)
                                             }
                                             fullWidth
                                          />
                                       </Grid>
                                       <Grid size={{ xs: 4 }}>
                                          <FormControl fullWidth size="small">
                                             <InputLabel>Rolle</InputLabel>
                                             <Select
                                                value={newMembershipRole}
                                                label="Rolle"
                                                onChange={(e) =>
                                                   setNewMembershipRole(e.target.value)
                                                }
                                             >
                                                <MenuItem value="admin">Administrator</MenuItem>
                                                <MenuItem value="readOnly">Nur lesen</MenuItem>
                                                <MenuItem value="checkOnly">Checken und lesen</MenuItem>
                                             </Select>
                                          </FormControl>
                                       </Grid>
                                       <Grid container size={{ xs: 2 }} justifyContent="center" >
                                          <IconButton
                                             color="primary"
                                             onClick={() =>
                                                handleAddMembership(membership.group.documentId)
                                             }
                                             sx={{
                                                marginLeft: "10px",
                                                color: "#10527e",
                                                "&:hover": {
                                                   backgroundColor: "rgba(25, 118, 210, 0.04)",
                                                },
                                             }}
                                          >
                                             <SaveIcon />
                                          </IconButton>
                                       </Grid>
                                    </Grid>
                                 </Grid>
                                 <hr style={{ borderTop: "1px solid #eee", marginTop: "15px", marginBottom: "15px" }} />
                                 <Box sx={{ width: "100%", justifyContent: "center", alignItems: "center", display: "flex" }}>
                                    <Button
                                       variant="outlined"
                                       color="error"
                                       startIcon={<DeleteIcon />}
                                       onClick={() => {
                                          if (window.confirm('Sind Sie sicher, dass Sie diese Gruppe löschen möchten?')) {
                                             handleDeleteGroup(membership.group.documentId);
                                          }
                                       }}
                                    >
                                       Gruppe löschen
                                    </Button>
                                 </Box>
                              </>
                           )}
                        </AccordionDetails>
                     </Accordion>
                     <br></br>
                  </Box>
               ))}
            </div>
         </div>

         <Dialog open={isAddGroupModalOpen} onClose={handleCloseAddGroupModal}>
            <DialogTitle>Neue Gruppe erstellen</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Bitte geben Sie den Namen und optional eine Beschreibung für die neue Gruppe ein.
               </DialogContentText>
               <TextField
                  autoFocus
                  margin="dense"
                  id="name"
                  label="Gruppenname"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  sx={{ mt: 2 }}
               />
               <TextField
                  margin="dense"
                  id="description"
                  label="Beschreibung (optional)"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  multiline
                  rows={3}
                  sx={{ mt: 2 }}
               />
               {errorMessage && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                     {errorMessage}
                  </Alert>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseAddGroupModal}>Abbrechen</Button>
               <Button
                  sx={{
                     backgroundColor: '#10527e',
                     color: 'white',
                     '&:hover': {
                        backgroundColor: '#282c34',
                     },
                     '&.Mui-disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666'
                     }
                  }}
                  onClick={handleAddGroup}
                  variant="contained">
                  Speichern
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
}